
import {defineComponent, ref} from "vue";
import FooterMod from "@/components/TopLevelView/FooterMod.vue";
import NavMod from "@/components/TopLevelView/NavMod.vue";
import CookieView from "@/components/TopLevelView/CookieView.vue";
import GradientBg3D from "@/components/shared/GradientBg3D.vue";
import {_cmsStore} from "@/stores/cmsStore";
import {introState} from "@/modules/introState";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import {DrawSVGPlugin} from "gsap/DrawSVGPlugin";

export default defineComponent({
  components: {NavMod, FooterMod, CookieView, GradientBg3D},
  data() {
    return {
      loadMod: ref<HTMLDivElement>(),
      loadTimeout : ref<ReturnType<typeof setTimeout>>(),
      onHome: -1,
      darkHero: false,
      navBlend: true,
      // first full page load: the overlay draws the mark, then hands off
      firstLoad: true,
      introComplete: false,   // the mark has finished drawing
      pageEntered: false,     // the first view emitted `enter` (data ready)
      revealSafety: ref<ReturnType<typeof setTimeout>>(),
    }
  },
  watch:{
    $route(){
      this.isHome()
    }
  },
  mounted(){
    this.loadMod = this.$refs.loadingScreen as HTMLDivElement;
    this.isHome();
    // no scrolling while the first-load intro runs — the flying y is
    // viewport-fixed while its landing spot is page-anchored, so a scroll
    // mid-flight would drop the mark into the wrong section
    this.lockScroll(true);
    this.playIntro();
  },
  methods: {
    isHome(){
      if(!this.$route.name) return;

      if(this.$route.name=== "HomeView") this.onHome = 1;
      else this.onHome = 0

      // pages with a full-screen dark hero want the transparent-at-top nav
      const darkHeroRoutes = [
        "AboutView", "ApproachView", "ApproachTopicView", "TeamPartnerView",
        "TechPolicyView", "VentureBuildingView", "CommunityBuildingView"
      ];
      this.darkHero = darkHeroRoutes.includes(this.$route.name as string);
    },
    showLoading(){
      // prune only the leaving view's triggers: once keep-alive swaps, its DOM
      // is detached — live views (and the entering one) keep theirs
      window.requestAnimationFrame(() => {
        for (const trigger of ScrollTrigger.getAll()){
          const el = trigger.trigger as Element | null;
          if (el && !el.isConnected) trigger.kill();
        }
      });

      // show the overlay only when the next view actually needs time — pages
      // with local data emit `enter` within a frame and never see it
      this.loadTimeout = window.setTimeout(() => {
        this.animateIn();
        this.loadTimeout = undefined;
      }, 150);

    },
    animateIn(){
      if(!this.loadMod) return;

      // route transitions: no zoom — the night field simply fades over, so
      // moving between the dark-hero pages reads as one continuous background
      gsap.set(this.loadMod.firstElementChild, { scale: 1, autoAlpha: 1 });

      // the mark's entrance belongs to the first-load intro only — on route
      // transitions the overlay is just a quiet backdrop, no logo replay
      gsap.set(this.loadMod.lastElementChild, { autoAlpha: 0 });

      gsap.fromTo(this.loadMod,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.25, ease: "linear" });
    },
    hideLoading(){
      // first full load: the intro overlay owns the reveal timing — it waits
      // for both the mark to finish drawing AND the page to be ready
      if(this.firstLoad){
        this.pageEntered = true;
        this.maybeReveal();
        return;
      }

      // subsequent route transitions: the simple fade
      if(!this.loadTimeout){
        // only hide Loadout when timeout is null
        this.animateOut();
      }else{
        // timeout is to be called
        // canceled Page Transition because new page was loaded within 10 ms
        window.clearTimeout(this.loadTimeout);
        this.loadTimeout = undefined;
      }

    },
    // opening choreography: the iconomy mark draws itself on the night
    // background. onComplete flips introComplete; reveal waits for the page too.
    playIntro(){
      const mark = this.$refs.loadMark as SVGSVGElement | undefined;
      const paths = mark ? mark.querySelectorAll(".introPath") : null;
      const reduced = typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // never trap the user behind the overlay if the view never emits `enter`
      this.revealSafety = window.setTimeout(() => {
        this.introComplete = true;
        this.pageEntered = true;
        this.maybeReveal();
      }, 6000);

      if(!paths || !paths.length || reduced){
        this.introComplete = true;
        this.maybeReveal();
        return;
      }

      gsap.registerPlugin(DrawSVGPlugin);
      gsap.set(paths, { fillOpacity: 0 });
      const tl = gsap.timeline({
        onComplete: () => { this.introComplete = true; this.maybeReveal(); }
      });
      tl.fromTo(paths,
        { drawSVG: "0%" },
        { drawSVG: "100%", duration: 1.2, stagger: 0.2, ease: "power1.inOut" });
      tl.to(paths, { fillOpacity: 1, duration: 0.45, ease: "power1.out" }, "-=0.25");
    },
    maybeReveal(){
      if(!this.firstLoad) return;
      if(this.introComplete && this.pageEntered) this.finishReveal();
    },
    finishReveal(){
      if(!this.firstLoad) return;
      this.firstLoad = false;
      if(this.revealSafety){ window.clearTimeout(this.revealSafety); this.revealSafety = undefined; }

      // 1. nav slides down from the top
      this.navBlend = false;
      // 2. landing page: the drawn y flies into the hero's corner and becomes
      //    its mark (no doubled y, the night field stays continuous). Anywhere
      //    else the overlay simply fades away. The flight keeps the scroll
      //    lock until the y has landed; the fade releases it right away.
      if(!this.flyMarkToHero()) {
        this.lockScroll(false);
        this.animateOut();
      }
      // 3. a beat later the hero statements are cleared to rise from the bottom
      window.setTimeout(() => {
        introState.done = true;
        introState.pending = false;
      }, 250);
    },
    // FLIP handoff, landing first load only: the overlay's mark flies from the
    // centre into the hero's bottom-right corner, scaling down onto the exact
    // spot of the hero's own y; the overlay's night field fades into the hero's
    // identical field underneath. Returns false when the flight can't run
    // (other route, scrolled, reduced motion) — caller falls back to the fade.
    flyMarkToHero(): boolean {
      if(this.$route.name !== "HomeView") return false;
      if(window.scrollY > 50) return false;
      if(typeof window.matchMedia === "function" &&
         window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;

      const mark = this.$refs.loadMark as SVGSVGElement | undefined;
      const heroMark = document.querySelector(".canvas .yMark") as HTMLElement | null;
      const heroSvg = heroMark ? heroMark.querySelector("svg") : null;
      if(!mark || !heroMark || !heroSvg || !this.loadMod) return false;

      introState.yHandoff = true;
      // the hero keeps its own y hidden until the flight lands on it
      gsap.set(heroMark, {autoAlpha: 0});

      const from = mark.getBoundingClientRect();
      const to = heroSvg.getBoundingClientRect();
      // glyph bounds inside the loading mark's 1024 box — the logo file has
      // empty margin, while the hero svg's viewBox is already glyph-exact
      const gx = 104.935 / 1024, gy = 11.13 / 1024, gw = 814.13 / 1024;
      const scale = to.width / (from.width * gw);
      const x = to.left - from.left - from.width * gx * scale;
      const y = to.top - from.top - from.height * gy * scale;

      // the overlay's solid ground + field must clear so the hero shows
      // through while the mark is still flying above it
      gsap.set(this.loadMod, {backgroundColor: "transparent", pointerEvents: "none"});

      const paths = mark.querySelectorAll(".introPath");
      const tl = gsap.timeline({
        onComplete: () => {
          // swap: hero's own mark takes over, overlay resets for route fades
          gsap.set(heroMark, {autoAlpha: 1});
          gsap.set(this.loadMod as HTMLDivElement, {autoAlpha: 0, clearProps: "backgroundColor,pointerEvents"});
          gsap.set(mark, {clearProps: "transform"});
          gsap.set(paths, {clearProps: "fill,stroke,strokeOpacity"});
          introState.yHandoff = false;
          this.lockScroll(false);
        }
      });

      // mark flies into the corner …
      tl.to(mark, {x, y, scale, transformOrigin: "0 0", duration: 1.15, ease: "power3.inOut"}, 0);
      // … turning fully accent-orange on the way (the hero mark is one colour)
      tl.to(paths, {fill: "#E65100", duration: 0.5, ease: "power1.inOut"}, 0.1);
      // … its drawing stroke fades away mid-flight: the stroke sits OUTSIDE
      // the fill bounds, so landing with it still on would make the mark
      // visibly shrink at the swap to the hero's stroke-free y
      tl.to(paths, {strokeOpacity: 0, duration: 0.45, ease: "power1.out"}, 0.3);
      // … while the overlay's field fades, revealing the hero's identical one
      tl.to(this.loadMod.firstElementChild, {autoAlpha: 0, duration: 0.7, ease: "linear"}, 0.15);

      return true;
    },
    // scroll lock for the first-load intro (html drives the page scroll)
    lockScroll(on: boolean){
      document.documentElement.style.overflow = on ? "hidden" : "";
    },
    animateOut(){
      if(!this.loadMod) return;

      gsap.to(this.loadMod, {
        autoAlpha: 0,
        duration: 0.3,
        ease: "linear"
      });
    },
    openSettings(){
      (this.$refs.cookieManager as InstanceType<typeof CookieView>).viewState = 2;
    }
  }
});

